import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  FileText, Plus, Trash2, Check, Users, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle2, Clock, UserX
} from 'lucide-react';
import { toast } from 'sonner';
import { useLiveMode } from '../context/LiveModeContext';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

export const LessonsPage = () => {
  const { classId } = useParams();
  const { session } = useLiveMode();
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [behaviourLogs, setBehaviourLogs] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [completions, setCompletions] = useState({});
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [newLesson, setNewLesson] = useState({ title: '', description: '' });

  const sessionId = session?.session_id;

  const fetchData = useCallback(async () => {
    try {
      const sessionParam = sessionId ? `?session_id=${sessionId}` : '';
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const [lessonsRes, classesRes, studentsRes, attRes, behRes] = await Promise.all([
        fetch(`${API}/lessons/${classId}${sessionParam}`, { credentials: 'include' }),
        fetch(`${API}/classes`, { credentials: 'include' }),
        fetch(`${API}/students/${classId}`, { credentials: 'include' }),
        fetch(`${API}/attendance/${classId}/${dateStr}${sessionParam}`, { credentials: 'include' }),
        fetch(`${API}/behaviour/${classId}${sessionParam}`, { credentials: 'include' }),
      ]);

      if (lessonsRes.ok) {
        const lessonData = await lessonsRes.json();
        setLessons(lessonData);
        // Fetch completions for all lessons
        for (const l of lessonData) {
          try {
            const cr = await fetch(`${API}/activity-completions/${l.progress_id}`, { credentials: 'include' });
            if (cr.ok) {
              const ids = await cr.json();
              setCompletions(prev => ({ ...prev, [l.progress_id]: ids }));
            }
          } catch (e) { /* ignore */ }
        }
      }
      if (classesRes.ok) {
        const classes = await classesRes.json();
        setClassInfo(classes.find(c => c.class_id === classId));
      }
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (attRes.ok) setAttendance(await attRes.json());
      if (behRes.ok) setBehaviourLogs(await behRes.json());
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [classId, sessionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Derived data
  const attendanceTaken = attendance.length > 0;
  const presentStudentIds = new Set(
    attendance.filter(a => a.status === 'present' || a.status === 'late').map(a => a.student_id)
  );
  const absentStudentIds = new Set(
    attendance.filter(a => a.status === 'absent').map(a => a.student_id)
  );
  // If attendance not taken, show ALL students as "present" for classwork tracking
  const presentStudents = attendanceTaken
    ? students.filter(s => presentStudentIds.has(s.student_id))
    : students;
  const absentStudents = attendanceTaken
    ? students.filter(s => absentStudentIds.has(s.student_id))
    : [];

  // Students with negative behaviour logs
  const negBehaviourStudentIds = new Set(
    behaviourLogs.filter(b => b.log_type === 'negative').map(b => b.student_id)
  );

  const handleCreateLesson = async () => {
    if (!newLesson.title.trim()) { toast.error('Activity title is required'); return; }
    try {
      const r = await fetch(`${API}/lessons`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...newLesson, class_id: classId, status: 'in_progress', order: lessons.length, session_id: sessionId || null })
      });
      if (r.ok) {
        toast.success('Activity added');
        setDialogOpen(false);
        setNewLesson({ title: '', description: '' });
        fetchData();
      }
    } catch (error) { toast.error('Failed to add activity'); }
  };

  const handleDeleteLesson = async (progressId) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      const r = await fetch(`${API}/lessons/${progressId}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) { toast.success('Activity deleted'); fetchData(); }
    } catch (error) { toast.error('Failed to delete'); }
  };

  const toggleStudentCompletion = async (progressId, studentId) => {
    const current = completions[progressId] || [];
    const updated = current.includes(studentId)
      ? current.filter(id => id !== studentId)
      : [...current, studentId];

    setCompletions(prev => ({ ...prev, [progressId]: updated }));

    try {
      await fetch(`${API}/activity-completions/${progressId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ progress_id: progressId, student_ids: updated })
      });
    } catch (error) { toast.error('Failed to save'); }
  };

  const markAllPresent = async (progressId) => {
    const presentIds = presentStudents.map(s => s.student_id);
    setCompletions(prev => ({ ...prev, [progressId]: presentIds }));

    try {
      await fetch(`${API}/activity-completions/${progressId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ progress_id: progressId, student_ids: presentIds })
      });
      toast.success('All present students marked completed');
    } catch (error) { toast.error('Failed to save'); }
  };

  const clearAll = async (progressId) => {
    setCompletions(prev => ({ ...prev, [progressId]: [] }));

    try {
      await fetch(`${API}/activity-completions/${progressId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ progress_id: progressId, student_ids: [] })
      });
      toast.success('Completions cleared');
    } catch (error) { toast.error('Failed to save'); }
  };

  // Stats per activity
  const getActivityStats = (progressId) => {
    const completed = (completions[progressId] || []).filter(id =>
      attendanceTaken ? presentStudentIds.has(id) : students.some(s => s.student_id === id)
    );
    const presentNotCompleted = presentStudents.filter(s => !completed.includes(s.student_id));
    return {
      completed: completed.length,
      presentNotCompleted: presentNotCompleted.length,
      present: presentStudents.length,
      absent: absentStudents.length,
      percent: presentStudents.length > 0 ? Math.round((completed.length / presentStudents.length) * 100) : 0,
    };
  };

  // Overall stats across all activities
  const overallStats = lessons.reduce((acc, l) => {
    const s = getActivityStats(l.progress_id);
    acc.totalCompleted += s.completed;
    acc.totalPresent += s.present;
    return acc;
  }, { totalCompleted: 0, totalPresent: 0 });
  const overallPercent = lessons.length > 0 && overallStats.totalPresent > 0
    ? Math.round((overallStats.totalCompleted / overallStats.totalPresent) * 100)
    : 0;

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: classInfo?.name || 'Class', path: '/app/live-mode' },
    { label: 'Classwork' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="space-y-5 max-w-3xl" data-testid="lessons-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-heading font-semibold text-slate-900">Classwork</h1>
            <p className="text-sm text-slate-500 mt-0.5">{classInfo?.name} {lessons.length > 0 && `— ${overallPercent}% completed`}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="add-lesson-btn">
                <Plus className="w-4 h-4 mr-1.5" />Add Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-2xl">
              <DialogHeader><DialogTitle className="font-heading text-lg text-slate-900">Add Class Activity</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Activity Title *</label>
                  <Input placeholder="e.g., Worksheet 3.2, Group Discussion" value={newLesson.title} onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })} className="rounded-xl border-slate-200" data-testid="lesson-title-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Description (optional)</label>
                  <Textarea placeholder="Activity details or instructions..." value={newLesson.description} onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })} className="rounded-xl border-slate-200 min-h-[60px] resize-none" />
                </div>
                <Button onClick={handleCreateLesson} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="create-lesson-btn">Add Activity</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Dashboard summary */}
        {lessons.length > 0 && (
          <Card className="bg-white border border-slate-200 shadow-soft" data-testid="classwork-dashboard">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-slate-500">Completion Overview</span>
                <span className="text-xs font-semibold text-teal-600">{overallPercent}%</span>
              </div>
              <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${overallPercent}%` }} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                  <p className="text-lg font-bold text-emerald-700" data-testid="completed-count">{overallStats.totalCompleted}</p>
                  <p className="text-[11px] text-emerald-600 font-medium">Completed</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-amber-50 border border-amber-100">
                  <p className="text-lg font-bold text-amber-700" data-testid="not-completed-count">{overallStats.totalPresent - overallStats.totalCompleted}</p>
                  <p className="text-[11px] text-amber-600 font-medium">Not Finished</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-lg font-bold text-slate-500" data-testid="absent-count">{absentStudents.length}</p>
                  <p className="text-[11px] text-slate-500 font-medium">Absent</p>
                </div>
              </div>
              {attendance.length === 0 && (
                <p className="text-xs text-amber-600 mt-3 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Attendance not taken yet — completions track all students
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Activities list */}
        {loading ? (
          <div className="text-center py-12 text-slate-400 text-sm">Loading...</div>
        ) : lessons.length === 0 ? (
          <Card className="bg-white border border-dashed border-slate-300 shadow-none">
            <CardContent className="py-12 text-center">
              <FileText className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-700 mb-1">No activities yet</h3>
              <p className="text-sm text-slate-400">Add class activities to track student progress</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {lessons.map((lesson) => {
              const stats = getActivityStats(lesson.progress_id);
              const isExpanded = expandedLesson === lesson.progress_id;
              const completedIds = completions[lesson.progress_id] || [];

              return (
                <Card key={lesson.progress_id} className="bg-white border border-slate-200 shadow-soft overflow-hidden" data-testid={`lesson-${lesson.progress_id}`}>
                  <CardContent className="p-0">
                    {/* Activity header */}
                    <div
                      className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedLesson(isExpanded ? null : lesson.progress_id)}
                      data-testid={`lesson-header-${lesson.progress_id}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        stats.percent === 100 ? 'bg-emerald-100' : stats.completed > 0 ? 'bg-amber-100' : 'bg-slate-100'
                      }`}>
                        {stats.percent === 100
                          ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          : stats.completed > 0
                            ? <Clock className="w-5 h-5 text-amber-600" />
                            : <FileText className="w-5 h-5 text-slate-400" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-slate-800 truncate">{lesson.title}</h3>
                        {lesson.description && <p className="text-xs text-slate-400 truncate mt-0.5">{lesson.description}</p>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg ${
                          stats.percent === 100 ? 'bg-emerald-100 text-emerald-700'
                          : stats.completed > 0 ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-500'
                        }`} data-testid={`lesson-stats-${lesson.progress_id}`}>
                          {stats.completed}/{stats.present}
                        </span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>

                    {/* Expanded student list */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 p-4 bg-slate-50/50" data-testid={`lesson-expanded-${lesson.progress_id}`}>
                        {/* Quick actions */}
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-medium text-slate-500">
                            {stats.completed} of {stats.present} present students completed
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); markAllPresent(lesson.progress_id); }}
                              className="text-xs font-medium text-emerald-600 hover:text-emerald-800"
                              data-testid={`mark-all-btn-${lesson.progress_id}`}
                            >All Completed</button>
                            <span className="text-slate-300">|</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); clearAll(lesson.progress_id); }}
                              className="text-xs font-medium text-slate-400 hover:text-slate-600"
                              data-testid={`clear-all-btn-${lesson.progress_id}`}
                            >Clear</button>
                          </div>
                        </div>

                        {/* Present students */}
                        {presentStudents.length > 0 ? (
                          <div className="space-y-1 mb-3">
                            {!attendanceTaken && (
                              <p className="text-xs text-amber-600 mb-2 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" /> Attendance not yet taken — showing all students
                              </p>
                            )}
                            {presentStudents.map(student => {
                              const isCompleted = completedIds.includes(student.student_id);
                              const hasNegBehaviour = negBehaviourStudentIds.has(student.student_id);
                              const flagged = hasNegBehaviour && !isCompleted;

                              return (
                                <button
                                  key={student.student_id}
                                  className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all text-left ${
                                    isCompleted
                                      ? 'bg-emerald-50 border-emerald-200'
                                      : flagged
                                        ? 'bg-red-50/50 border-red-200'
                                        : 'bg-white border-slate-150 hover:bg-slate-50'
                                  }`}
                                  onClick={(e) => { e.stopPropagation(); toggleStudentCompletion(lesson.progress_id, student.student_id); }}
                                  data-testid={`student-toggle-${student.student_id}-${lesson.progress_id}`}
                                >
                                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                    isCompleted ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 bg-white'
                                  }`}>
                                    {isCompleted && <Check className="w-3.5 h-3.5 text-white" />}
                                  </div>
                                  <span className={`text-sm flex-1 ${isCompleted ? 'text-emerald-700 font-medium' : 'text-slate-700'}`}>
                                    {student.name}
                                  </span>
                                  {flagged && (
                                    <span className="text-[10px] text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-md font-medium flex items-center gap-1" data-testid={`flagged-${student.student_id}`}>
                                      <AlertTriangle className="w-3 h-3" /> Needs attention
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 mb-3">No students in this class yet.</p>
                        )}

                        {/* Absent students (excluded) */}
                        {absentStudents.length > 0 && (
                          <div className="pt-2 border-t border-slate-100">
                            <p className="text-[11px] font-medium text-slate-400 mb-1.5 flex items-center gap-1">
                              <UserX className="w-3 h-3" /> Absent — excluded ({absentStudents.length})
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {absentStudents.map(s => (
                                <span key={s.student_id} className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md">{s.name}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Delete */}
                        <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteLesson(lesson.progress_id); }}
                            className="text-xs text-red-400 hover:text-red-600 font-medium flex items-center gap-1"
                            data-testid={`delete-btn-${lesson.progress_id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Remove activity
                          </button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};
